import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { PRACTICE_TYPE_ICONS, PRACTICE_TYPE_LABELS, type Pillar, type Practice } from '../api/types';
import { useUser } from '../state/UserContext';
import { TRADITION_LABELS, type Tradition } from '../theme/tokens';

/** One pillar's ordered level path, plus the practices that belong to it. */
interface PillarPath {
  pillar: Pillar;
  levels: Practice[];
}

function LevelPath({
  levels,
  completed,
  onOpen,
}: {
  levels: Practice[];
  completed: Set<string>;
  onOpen: (practiceId: string) => void;
}) {
  return (
    <div className="level-path">
      {levels.map((p, i) => {
        const isDone = completed.has(p.practiceId);
        const isProgressLocked = i > 0 && !completed.has(levels[i - 1].practiceId);
        const isPremiumLocked = p.premiumLocked && !isDone;
        const isLocked = isProgressLocked || isPremiumLocked;
        return (
          <div key={p.practiceId} className="level-row">
            {i > 0 && <span className={`level-connector${isLocked ? '' : ' filled'}`} aria-hidden="true" />}
            <button
              type="button"
              className={`level-node${isDone ? ' done' : ''}${isLocked ? ' locked' : ''}${isPremiumLocked ? ' premium' : ''}`}
              disabled={isLocked}
              onClick={() => onOpen(p.practiceId)}
            >
              <span className="level-node-badge" aria-hidden="true">
                {isDone ? '✓' : isPremiumLocked ? '💎' : isProgressLocked ? '🔒' : PRACTICE_TYPE_ICONS[p.type]}
              </span>
              <span className="level-node-info">
                <span className="level-node-title">{p.title}</span>
                <span className="level-node-meta">
                  Level {p.level ?? i + 1} &middot; {PRACTICE_TYPE_LABELS[p.type]} &middot;{' '}
                  {Math.round(p.durationSeconds / 60)} min
                  {isPremiumLocked && <> &middot; Premium ($9.99/mo)</>}
                </span>
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function Practices() {
  const { me } = useUser();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Tradition | null>(null);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const options = me?.traditions ?? [];

  // Default the tradition tab to the user's main practice once we know it.
  useEffect(() => {
    if (selected || options.length === 0) return;
    setSelected(me?.primaryTradition && options.includes(me.primaryTradition) ? me.primaryTradition : options[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.join(','), me?.primaryTradition]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.getPractices(selected ? { tradition: selected } : {}),
      api.getPillars(selected ? { tradition: selected } : {}),
    ])
      .then(([practicesRes, pillarsRes]) => {
        if (cancelled) return;
        setPractices(practicesRes.practices);
        setPillars(pillarsRes.pillars);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load practices.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selected]);

  // A tradition with pillar rows renders one level-path per pillar, each ordered by its
  // author-assigned `level`. A tradition with none yet (not migrated onto the pillar model)
  // falls back to the legacy flat path, ordered by duration -- unchanged from before.
  const pillarPaths: PillarPath[] = useMemo(() => {
    if (pillars.length === 0) return [];
    return pillars
      .map((pillar) => ({
        pillar,
        levels: practices
          .filter((p) => p.pillarId === pillar.pillarId)
          .sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
      }))
      .filter((pp) => pp.levels.length > 0);
  }, [pillars, practices]);

  const legacyPath = useMemo(
    () => [...practices].sort((a, b) => a.durationSeconds - b.durationSeconds || a.practiceId.localeCompare(b.practiceId)),
    [practices],
  );

  const completed = new Set(me?.completedPracticeIds ?? []);
  const usingPillars = pillarPaths.length > 0;

  return (
    <div>
      <h1 className="page-title">Practices</h1>
      <p className="page-subtitle">
        A guided path through your chosen tradition &mdash; meditations, prayers, and readings,
        one level at a time.
      </p>

      {options.length > 1 && (
        <div className="tradition-tabs">
          {options.map((t) => (
            <button
              key={t}
              type="button"
              className={`tradition-tab${selected === t ? ' active' : ''}`}
              onClick={() => setSelected(t)}
            >
              {TRADITION_LABELS[t] ?? t}
            </button>
          ))}
        </div>
      )}

      {error && <p className="error-text">{error}</p>}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : usingPillars ? (
        pillarPaths.map(({ pillar, levels }) => (
          <section key={pillar.pillarId} className="pillar-section">
            <h2 className="pillar-title">
              <span aria-hidden="true">{PRACTICE_TYPE_ICONS[pillar.practiceType]}</span> {pillar.title}
            </h2>
            <p className="pillar-description">{pillar.description}</p>
            <LevelPath levels={levels} completed={completed} onOpen={(id) => navigate(`/practice/${id}`)} />
          </section>
        ))
      ) : legacyPath.length === 0 ? (
        <div className="empty-state">
          No practices match your traditions yet. Try adding more in Settings.
        </div>
      ) : (
        <LevelPath levels={legacyPath} completed={completed} onOpen={(id) => navigate(`/practice/${id}`)} />
      )}
    </div>
  );
}
